import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, GiftIcon, ArrowRightIcon, ZapIcon, TrophyIcon, StarIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
interface SpinWheelPopupProps {
  isOpen: boolean;
  onClose: () => void;
  isLoggedIn?: boolean;
}
interface Segment {
  label: string;
  emoji: string;
  color: string;
}
const SEGMENTS: Segment[] = [{
  label: '1.000đ',
  emoji: '🎫',
  color: '#6B7280'
}, {
  label: 'Áo Thun',
  emoji: '👕',
  color: '#8B5CF6'
}, {
  label: '10.000đ',
  emoji: '💵',
  color: '#D97706'
}, {
  label: 'Nhớt Free',
  emoji: '🔧',
  color: '#2563EB'
}, {
  label: '50.000đ',
  emoji: '💰',
  color: '#16A34A'
}, {
  label: '500K ★',
  emoji: '🏆',
  color: '#DC2626'
}, {
  label: '5.000đ',
  emoji: '🎟️',
  color: '#DB2777'
}, {
  label: 'Nón MHP',
  emoji: '🧢',
  color: '#EA580C'
}];
const WINNING_INDEX = 5;
const SEG_ANGLE = 360 / SEGMENTS.length; // 45 degrees
// Build conic-gradient string
function buildConicGradient(): string {
  const stops = SEGMENTS.map((seg, i) => {
    const start = i * SEG_ANGLE;
    const end = (i + 1) * SEG_ANGLE;
    return `${seg.color} ${start}deg ${end}deg`;
  });
  return `conic-gradient(from -90deg, ${stops.join(', ')})`;
}
export function SpinWheelPopup({
  isOpen,
  onClose,
  isLoggedIn
}: SpinWheelPopupProps) {
  const [phase, setPhase] = useState<'ready' | 'spinning' | 'won'>('ready');
  const [rotation, setRotation] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showFlash, setShowFlash] = useState(false);
  const wheelSize = 270;
  useEffect(() => {
    if (isOpen) {
      setPhase('ready');
      setRotation(0);
      setShowConfetti(false);
      setShowFlash(false);
    }
  }, [isOpen]);
  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  const handleSpin = useCallback(() => {
    if (phase !== 'ready') return;
    setPhase('spinning');
    const targetCenter = WINNING_INDEX * SEG_ANGLE + SEG_ANGLE / 2;
    const targetAngle = 360 - targetCenter;
    const fullSpins = 360 * (10 + Math.random() * 2);
    const finalRotation = fullSpins + targetAngle + (Math.random() * 8 - 4);
    setRotation(finalRotation);
    setTimeout(() => {
      setShowFlash(true);
      setTimeout(() => setShowFlash(false), 300);
    }, 6200);
    setTimeout(() => {
      setPhase('won');
      setShowConfetti(true);
    }, 6500);
  }, [phase]);
  const confettiParticles = useMemo(() => {
    if (!showConfetti) return [];
    const colors = ['#C8A84E', '#EF4444', '#22C55E', '#3B82F6', '#EC4899', '#F59E0B', '#FFD700', '#8B5CF6'];
    return Array.from({
      length: 80
    }, (_, i) => ({
      id: i,
      x: 40 + Math.random() * 20,
      startY: 30 + Math.random() * 20,
      delay: Math.random() * 0.8,
      duration: 2 + Math.random() * 2,
      color: colors[Math.floor(Math.random() * colors.length)],
      size: 3 + Math.random() * 8,
      shape: Math.random() > 0.5 ? 'square' : 'circle',
      drift: (Math.random() - 0.5) * 200,
      rotateEnd: Math.random() * 720
    }));
  }, [showConfetti]);
  // Segment labels positioned around the wheel
  const segmentLabels = SEGMENTS.map((seg, i) => {
    const angleDeg = i * SEG_ANGLE + SEG_ANGLE / 2 - 90; // -90 because conic starts at top
    const angleRad = angleDeg * Math.PI / 180;
    const labelRadius = wheelSize * 0.34;
    const x = Math.cos(angleRad) * labelRadius;
    const y = Math.sin(angleRad) * labelRadius;
    return <div key={i} className="absolute flex flex-col items-center pointer-events-none" style={{
      left: '50%',
      top: '50%',
      transform: `translate(-50%, -50%) translate(${x}px, ${y}px) rotate(${angleDeg + 90}deg)`
    }} data-id="element-486">
        <span className="text-lg leading-none" style={{
        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))'
      }} data-id="element-487">
          {seg.emoji}
        </span>
        <span className={`text-[9px] font-bold text-white mt-0.5 whitespace-nowrap ${i === WINNING_INDEX ? 'text-[10px] text-yellow-200' : ''}`} style={{
        textShadow: '0 1px 3px rgba(0,0,0,0.8)'
      }} data-id="element-488">
          {seg.label}
        </span>
        {i === WINNING_INDEX && <span className="text-[7px] font-black text-yellow-300 tracking-wider" style={{
        textShadow: '0 1px 3px rgba(0,0,0,0.8)'
      }} data-id="element-489">
            JACKPOT
          </span>}
      </div>;
  });
  return <AnimatePresence data-id="element-490">
      {isOpen && <div className="fixed inset-0 z-[60] flex items-center justify-center p-3" data-id="element-491">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={phase === 'spinning' ? undefined : onClose} data-id="element-492" />

          {/* Win flash */}
          <AnimatePresence data-id="element-493">
            {showFlash && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 0.6
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.15
        }} className="absolute inset-0 bg-[#C8A84E] z-[61] pointer-events-none" data-id="element-494" />}
          </AnimatePresence>

          {/* Confetti */}
          <AnimatePresence data-id="element-495">
            {showConfetti && <div className="absolute inset-0 pointer-events-none overflow-hidden z-[63]" data-id="element-496">
                {confettiParticles.map(p => <motion.div key={p.id} initial={{
            x: `${p.x}%`,
            y: `${p.startY}%`,
            opacity: 1,
            rotate: 0,
            scale: 0
          }} animate={{
            y: '120%',
            x: `calc(${p.x}% + ${p.drift}px)`,
            rotate: p.rotateEnd,
            opacity: [0, 1, 1, 1, 0],
            scale: [0, 1.5, 1, 1, 0.5]
          }} transition={{
            duration: p.duration,
            delay: p.delay,
            ease: 'easeOut'
          }} className="absolute" style={{
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
            borderRadius: p.shape === 'circle' ? '50%' : '2px'
          }} data-id="element-497" />)}
              </div>}
          </AnimatePresence>

          {/* Main popup */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.7,
        y: 40
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.7,
        y: 40
      }} transition={{
        type: 'spring',
        stiffness: 350,
        damping: 25
      }} className="relative z-[64] w-full max-w-[400px]" data-id="element-498">
            {/* Close */}
            <button onClick={onClose} className="absolute -top-3 -right-3 z-10 w-9 h-9 bg-[#2A2A2A] border border-[#444] rounded-full flex items-center justify-center text-[#888] hover:text-white hover:bg-[#444] transition-colors shadow-lg" aria-label="Đóng" data-id="element-499">
              <XIcon className="w-4 h-4" data-id="element-500" />
            </button>

            <div className="bg-gradient-to-b from-[#1E1E1E] to-[#111] border-2 border-[#C8A84E]/40 rounded-2xl overflow-hidden shadow-[0_0_60px_rgba(200,168,78,0.15)]" data-id="element-501">
              {/* Header */}
              <div className="relative bg-gradient-to-r from-[#EF4444]/25 via-[#C8A84E]/30 to-[#EF4444]/25 px-5 py-3.5 text-center border-b border-[#C8A84E]/30" data-id="element-502">
                <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent" animate={{
              x: ['-100%', '100%']
            }} transition={{
              duration: 1.8,
              repeat: Infinity,
              ease: 'linear'
            }} data-id="element-503" />
                <div className="relative" data-id="element-504">
                  <div className="flex items-center justify-center gap-2 mb-0.5" data-id="element-505">
                    <motion.span animate={{
                  rotate: [0, 20, -20, 0]
                }} transition={{
                  duration: 1.2,
                  repeat: Infinity
                }} className="text-2xl" data-id="element-506">
                      🎰
                    </motion.span>
                    <h2 className="text-xl font-black text-white tracking-tight" data-id="element-507">
                      VÒNG QUAY MAY MẮN
                    </h2>
                    <motion.span animate={{
                  rotate: [0, -20, 20, 0]
                }} transition={{
                  duration: 1.2,
                  repeat: Infinity
                }} className="text-2xl" data-id="element-508">
                      🎁
                    </motion.span>
                  </div>
                  <p className="text-xs text-[#FFD700] font-bold tracking-wide" data-id="element-509">
                    QUAY MIỄN PHÍ — TRÚNG THƯỞNG 100%!
                  </p>
                </div>
              </div>

              {/* Prize ticker */}
              <div className="bg-[#0D0D0D] py-1.5 px-4 border-b border-[#2A2A2A] overflow-hidden" data-id="element-510">
                <motion.div className="flex gap-6 whitespace-nowrap" animate={{
              x: ['0%', '-50%']
            }} transition={{
              duration: 12,
              repeat: Infinity,
              ease: 'linear'
            }} data-id="element-511">
                  {[...SEGMENTS, ...SEGMENTS].map((seg, i) => <span key={i} className="text-[10px] text-[#888] flex items-center gap-1" data-id="element-512">
                      <span data-id="element-513">{seg.emoji}</span>
                      <span data-id="element-514">{seg.label}</span>
                    </span>)}
                </motion.div>
              </div>

              {/* Wheel area */}
              <div className="px-4 pt-4 pb-2 flex justify-center" data-id="element-515">
                <div className="relative" style={{
              width: wheelSize + 40,
              height: wheelSize + 40
            }} data-id="element-516">
                  {/* Glow */}
                  <motion.div className="absolute inset-0 rounded-full" style={{
                background: 'radial-gradient(circle, rgba(200,168,78,0.2) 0%, transparent 70%)'
              }} animate={{
                scale: [1, 1.08, 1],
                opacity: [0.5, 1, 0.5]
              }} transition={{
                duration: 2,
                repeat: Infinity
              }} data-id="element-517" />

                  {/* Pointer */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 z-20" style={{
                marginTop: -2
              }} data-id="element-518">
                    <motion.div animate={phase === 'spinning' ? {
                  scale: [1, 1.3, 1]
                } : {}} transition={{
                  duration: 0.3,
                  repeat: phase === 'spinning' ? Infinity : 0
                }} data-id="element-519">
                      <div style={{
                    width: 0,
                    height: 0,
                    borderLeft: '14px solid transparent',
                    borderRight: '14px solid transparent',
                    borderTop: '26px solid #C8A84E',
                    filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.6))'
                  }} data-id="element-520" />
                    </motion.div>
                  </div>

                  {/* LED lights */}
                  {Array.from({
                length: 20
              }).map((_, i) => {
                const angle = i / 20 * 360;
                const rad = angle * Math.PI / 180;
                const r = wheelSize / 2 + 17;
                const cx = wheelSize / 2 + 20;
                const cy = wheelSize / 2 + 20;
                return <motion.div key={i} className="absolute rounded-full z-[5]" style={{
                  width: 7,
                  height: 7,
                  left: cx + Math.cos(rad) * r - 3.5,
                  top: cy + Math.sin(rad) * r - 3.5,
                  backgroundColor: i % 3 === 0 ? '#FFD700' : i % 3 === 1 ? '#EF4444' : '#22C55E',
                  boxShadow: `0 0 8px ${i % 3 === 0 ? '#FFD700' : i % 3 === 1 ? '#EF4444' : '#22C55E'}`
                }} animate={{
                  opacity: phase === 'spinning' ? [0.2, 1, 0.2] : [0.4, 1, 0.4],
                  scale: [0.8, 1.3, 0.8]
                }} transition={{
                  duration: phase === 'spinning' ? 0.15 : 1,
                  repeat: Infinity,
                  delay: i * (phase === 'spinning' ? 0.015 : 0.05)
                }} data-id="element-521" />;
              })}

                  {/* THE WHEEL — CSS conic-gradient */}
                  <motion.div className="absolute rounded-full z-10 overflow-hidden" style={{
                width: wheelSize,
                height: wheelSize,
                left: 20,
                top: 20,
                background: buildConicGradient(),
                boxShadow: '0 0 0 5px #1A1A1A, 0 0 0 7px #C8A84E, 0 0 0 10px #1A1A1A, 0 10px 40px rgba(0,0,0,0.6)'
              }} animate={{
                rotate: rotation
              }} transition={{
                duration: phase === 'spinning' ? 6 : 0,
                ease: [0.15, 0.85, 0.25, 1]
              }} data-id="element-522">
                    {/* Segment divider lines */}
                    {SEGMENTS.map((_, i) => <div key={i} className="absolute top-0 left-1/2 origin-bottom" style={{
                  width: 1.5,
                  height: wheelSize / 2,
                  backgroundColor: 'rgba(0,0,0,0.3)',
                  transform: `translateX(-50%) rotate(${i * SEG_ANGLE}deg)`,
                  transformOrigin: `50% ${wheelSize / 2}px`
                }} data-id="element-523" />)}

                    {/* Inner shine overlay */}
                    <div className="absolute inset-0 rounded-full" style={{
                  background: 'radial-gradient(circle at 35% 35%, rgba(255,255,255,0.15) 0%, transparent 50%)'
                }} data-id="element-524" />

                    {/* Segment labels */}
                    {segmentLabels}

                    {/* Center hub */}
                    <div className="absolute rounded-full z-20 flex flex-col items-center justify-center" style={{
                  width: 52,
                  height: 52,
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  background: 'radial-gradient(circle, #FFE066 0%, #C8A84E 50%, #8B7340 100%)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.4)'
                }} data-id="element-525">
                      <span className="text-[#0D0D0D] font-black text-[9px] leading-none" data-id="element-526">
                        QUAY
                      </span>
                      <span className="text-[#0D0D0D] font-black text-[8px] leading-none mt-0.5" data-id="element-527">
                        NGAY!
                      </span>
                    </div>
                  </motion.div>
                </div>
              </div>

              {/* Action area */}
              <div className="px-5 pb-5 pt-2" data-id="element-528">
                <AnimatePresence mode="wait" data-id="element-529">
                  {phase === 'ready' && <motion.div key="ready" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} className="text-center" data-id="element-530">
                      <motion.button onClick={handleSpin} className="w-full py-4 bg-gradient-to-r from-[#EF4444] via-[#FF6B35] to-[#EF4444] rounded-xl text-white font-black text-lg shadow-lg relative overflow-hidden" whileHover={{
                  scale: 1.03
                }} whileTap={{
                  scale: 0.97
                }} animate={{
                  boxShadow: ['0 4px 20px rgba(239,68,68,0.3)', '0 4px 35px rgba(255,107,53,0.5)', '0 4px 20px rgba(239,68,68,0.3)']
                }} transition={{
                  duration: 1.5,
                  repeat: Infinity
                }} data-id="element-531">
                        <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent" animate={{
                    x: ['-100%', '100%']
                  }} transition={{
                    duration: 1,
                    repeat: Infinity
                  }} data-id="element-532" />
                        <span className="relative flex items-center justify-center gap-2" data-id="element-533">
                          <ZapIcon className="w-5 h-5" data-id="element-534" />
                          QUAY NGAY — MIỄN PHÍ!
                          <ZapIcon className="w-5 h-5" data-id="element-535" />
                        </span>
                      </motion.button>
                      <div className="flex items-center justify-center gap-2 mt-2" data-id="element-536">
                        <motion.div className="w-1.5 h-1.5 rounded-full bg-[#22C55E]" animate={{
                    opacity: [1, 0.3, 1]
                  }} transition={{
                    duration: 1,
                    repeat: Infinity
                  }} data-id="element-537" />
                        <span className="text-[10px] text-[#22C55E] font-medium" data-id="element-538">
                          Trúng thưởng 100% — Chỉ 1 lần duy nhất!
                        </span>
                      </div>
                    </motion.div>}

                  {phase === 'spinning' && <motion.div key="spinning" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} className="text-center py-3" data-id="element-539">
                      <div className="flex items-center justify-center gap-3" data-id="element-540">
                        <motion.span className="text-2xl" animate={{
                    rotate: 360
                  }} transition={{
                    duration: 0.5,
                    repeat: Infinity,
                    ease: 'linear'
                  }} data-id="element-541">
                          🎰
                        </motion.span>
                        <motion.p className="text-[#FFD700] font-black text-lg tracking-wide" animate={{
                    opacity: [1, 0.4, 1],
                    scale: [1, 1.05, 1]
                  }} transition={{
                    duration: 0.6,
                    repeat: Infinity
                  }} data-id="element-542">
                          ĐANG QUAY...
                        </motion.p>
                        <motion.span className="text-2xl" animate={{
                    rotate: -360
                  }} transition={{
                    duration: 0.5,
                    repeat: Infinity,
                    ease: 'linear'
                  }} data-id="element-543">
                          🎰
                        </motion.span>
                      </div>
                    </motion.div>}

                  {phase === 'won' && <motion.div key="won" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className="text-center" data-id="element-544">
                      <motion.div initial={{
                  scale: 0,
                  rotate: -10
                }} animate={{
                  scale: 1,
                  rotate: 0
                }} transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 15,
                  delay: 0.1
                }} className="mb-3" data-id="element-545">
                        <motion.div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#EF4444] via-[#FFD700] to-[#EF4444] rounded-full shadow-lg" animate={{
                    boxShadow: ['0 0 20px rgba(255,215,0,0.3)', '0 0 40px rgba(255,215,0,0.6)', '0 0 20px rgba(255,215,0,0.3)']
                  }} transition={{
                    duration: 1,
                    repeat: Infinity
                  }} data-id="element-546">
                          <TrophyIcon className="w-5 h-5 text-[#0D0D0D]" data-id="element-547" />
                          <span className="text-[#0D0D0D] font-black text-sm tracking-wider" data-id="element-548">
                            🎉 JACKPOT! 🎉
                          </span>
                          <TrophyIcon className="w-5 h-5 text-[#0D0D0D]" data-id="element-549" />
                        </motion.div>
                      </motion.div>

                      <motion.p initial={{
                  opacity: 0,
                  y: 15
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.4
                }} className="text-white text-sm font-medium mb-1" data-id="element-550">
                        Bạn đã trúng giải thưởng lớn nhất!
                      </motion.p>

                      <motion.div initial={{
                  opacity: 0,
                  scale: 0.3
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 12,
                  delay: 0.6
                }} className="my-3" data-id="element-551">
                        <motion.div animate={{
                    scale: [1, 1.05, 1]
                  }} transition={{
                    duration: 1.5,
                    repeat: Infinity
                  }} data-id="element-552">
                          <span className="text-5xl font-black bg-gradient-to-r from-[#FFD700] via-[#FFF] to-[#FFD700] bg-clip-text text-transparent" data-id="element-553">
                            500.000đ
                          </span>
                        </motion.div>
                        <p className="text-[#C8A84E] font-bold text-base mt-1" data-id="element-554">
                          🏆 TÍN DỤNG MUA HÀNG 🏆
                        </p>
                      </motion.div>

                      <motion.p initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  delay: 0.9
                }} className="text-xs text-[#B0B0B0] mb-4" data-id="element-555">
                        Tương đương{' '}
                        <span className="text-[#22C55E] font-bold" data-id="element-556">
                          1.000 điểm thưởng
                        </span>{' '}
                        — gấp 5 lần thưởng đăng ký!
                      </motion.p>

                      <motion.div initial={{
                  opacity: 0,
                  y: 12
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 1.1
                }} data-id="element-557">
                        <Link to={isLoggedIn ? '/vi' : '/dang-nhap'} onClick={() => {
                    // Track jackpot bonus for signup
                    sessionStorage.setItem('mhp_signup_bonus', 'jackpot');
                    onClose();
                  }} data-id="element-558">
                          <motion.button className="w-full py-4 bg-gradient-to-r from-[#C8A84E] via-[#FFD700] to-[#C8A84E] rounded-xl text-[#0D0D0D] font-black text-base shadow-xl relative overflow-hidden" whileHover={{
                      scale: 1.02
                    }} whileTap={{
                      scale: 0.98
                    }} animate={{
                      boxShadow: ['0 4px 20px rgba(200,168,78,0.3)', '0 4px 40px rgba(255,215,0,0.5)', '0 4px 20px rgba(200,168,78,0.3)']
                    }} transition={{
                      duration: 1.5,
                      repeat: Infinity
                    }} data-id="element-559">
                            <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent" animate={{
                        x: ['-100%', '100%']
                      }} transition={{
                        duration: 1.5,
                        repeat: Infinity
                      }} data-id="element-560" />
                            <span className="relative flex items-center justify-center gap-2" data-id="element-561">
                              <GiftIcon className="w-5 h-5" data-id="element-562" />
                              {isLoggedIn ? 'NHẬN 500.000đ NGAY!' : 'ĐĂNG KÝ NHẬN 500.000đ!'}
                              <ArrowRightIcon className="w-5 h-5" data-id="element-563" />
                            </span>
                          </motion.button>
                        </Link>
                        <div className="flex items-center justify-center gap-4 mt-3 text-[10px] text-[#888]" data-id="element-564">
                          <span className="flex items-center gap-1" data-id="element-565">
                            <StarIcon className="w-3 h-3 text-[#22C55E]" data-id="element-566" />
                            Miễn phí 100%
                          </span>
                          <span className="flex items-center gap-1" data-id="element-567">
                            <StarIcon className="w-3 h-3 text-[#22C55E]" data-id="element-568" />
                            30 giây đăng ký
                          </span>
                          <span className="flex items-center gap-1" data-id="element-569">
                            <StarIcon className="w-3 h-3 text-[#22C55E]" data-id="element-570" />
                            Nhận ngay
                          </span>
                        </div>
                      </motion.div>

                      <motion.div initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  delay: 1.5
                }} className="mt-3 flex items-center justify-center gap-1.5" data-id="element-571">
                        <motion.div className="w-2 h-2 rounded-full bg-[#EF4444]" animate={{
                    opacity: [1, 0.2, 1],
                    scale: [1, 1.3, 1]
                  }} transition={{
                    duration: 0.8,
                    repeat: Infinity
                  }} data-id="element-572" />
                        <span className="text-[11px] text-[#EF4444] font-bold" data-id="element-573">
                          ⏰ Phần thưởng hết hạn sau 15 phút!
                        </span>
                      </motion.div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}